import type { CountryCode } from "libphonenumber-js"
import type { ChangeEvent } from "react"

import { formatIncompletePhoneNumber, getCountries, getCountryCallingCode, getExampleNumber } from "libphonenumber-js"
import { parsePhoneNumber } from "libphonenumber-js/min"
import examples from "libphonenumber-js/mobile/examples"
import { useEffect, useRef, useState } from "react"

import type { TextFieldProps } from "../.."

import styles from "../../index.module.css"
import { BaseField } from "../baseField"
import { FLAG_EMOJIS } from "./config"

export const PhoneField = ({
  label,
  name,
  countryCodes,
  defaultCountryCode,
  errorText,
  ...inputProps
}: TextFieldProps) => {
  const [countryCode, setCountryCode] = useState<CountryCode>(
    (defaultCountryCode as CountryCode) || ("ES" as CountryCode),
  )
  const [selectWidth, setSelectWidth] = useState<number>(0)

  const inputRef = useRef<HTMLInputElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    if (selectRef.current) {
      const width = selectRef.current.getBoundingClientRect().width
      setSelectWidth(width)
    }
  }, [countryCode])

  useEffect(() => {
    if (inputRef.current && !inputRef.current.value) {
      const callingCode = getCountryCallingCode(countryCode)
      inputRef.current.value = `+${callingCode} `

      if (inputProps.onChange) {
        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
        } as ChangeEvent<HTMLInputElement>
        inputProps.onChange(syntheticEvent)
      }
    }
  }, [countryCode, inputProps])

  const placeholder = formatIncompletePhoneNumber(
    getExampleNumber(countryCode, examples)?.formatInternational() || "",
    countryCode,
  )

  const defaultCountries = countryCodes ?? getCountries()

  const countryOptions = defaultCountries.map((country) => ({
    code: country,
    callingCode: getCountryCallingCode(country as CountryCode),
    name: country.toUpperCase(),
  }))

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newCountryCode = e.target.value as CountryCode
    setCountryCode(newCountryCode)

    if (inputRef.current) {
      const callingCode = getCountryCallingCode(newCountryCode)
      const currentValue = inputRef.current.value

      const cleanValue = currentValue.replace(/^\+\d+\s*/, "")
      inputRef.current.value = `+${callingCode} ${cleanValue}`.trim()

      if (inputProps.onChange) {
        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
        } as ChangeEvent<HTMLInputElement>
        inputProps.onChange(syntheticEvent)
      }
    }
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const phoneNumber = parsePhoneNumber(input.value, countryCode)
    const isValid = phoneNumber.isValid()

    if (!isValid) {
      input.setCustomValidity("Invalid phone number")
    } else {
      input.setCustomValidity("")
    }

    if (inputRef.current) inputRef.current.value = phoneNumber.formatInternational()

    if (inputProps.onChange) {
      inputProps.onChange?.(e)
    }
  }

  return (
    <BaseField
      label={label}
      errorText={errorText}
      required={inputProps.required}
      disabled={inputProps.disabled}
      id={inputProps.id}
    >
      <div className={styles.countrySelectWrapper}>
        <select
          defaultValue={defaultCountryCode}
          ref={selectRef}
          className={styles.select}
          value={countryCode}
          onChange={handleCountryChange}
        >
          {countryOptions.map((country) => (
            <option key={country.code} value={country.code}>
              {FLAG_EMOJIS[country.code as keyof typeof FLAG_EMOJIS]} {country.name}
            </option>
          ))}
        </select>
      </div>
      <input
        {...inputProps}
        ref={inputRef}
        name={name}
        placeholder={placeholder}
        onChange={handleOnChange}
        className={styles.input}
        style={{ paddingLeft: `calc(${selectWidth}px + var(--size-s))` }}
      />
    </BaseField>
  )
}
